import {ChevronDownIcon, ChevronRightIcon} from "@heroicons/react/24/outline";
import React, {useEffect, useState} from "react";
import {genericMoneyFormatter} from "../../../../common/util/util-vanilla";
import {classNames} from "../../../../common/util/util-helpers";

export default function ComparisonListItem({
                                               name,
                                               data,
                                               isSectionCollapsed,
                                               onToggleSectionClick,
                                               expandedAllCount,
                                               collapsedAllCount,
                                               columns,
                                               handleToggleAccountDetails
                                           }) {
    const numberOfColumns = Object.keys(columns).length + 1;

    let sectionTotals = {};
    const childrenItems = {};
    const accountTypeList = {}
    const parentItems = Object.keys(data).reduce((memo, dataKey) => {
        const row = Object.values(data[dataKey]);
        const item = row[0];

        if (!!data[dataKey] && item.AccountType === name) {
            if (!item.ParentAccountID) {
                if (row.find(it => it.Value)) {
                    memo.push(data[dataKey]);
                }
            } else {
                if (row.find(it => it.Value)) {
                    childrenItems[item.ParentAccountID] = data[dataKey];
                }
            }

            sectionTotals = Object.keys(data[dataKey]).reduce((totalMemo, colIndex) => {
                if (!totalMemo[colIndex]) {
                    totalMemo[colIndex] = 0;
                }
                totalMemo[colIndex] += data[dataKey][colIndex].Value ?? 0;
                return totalMemo;
            }, sectionTotals);

            if (!accountTypeList[item.AccountType]) {
                accountTypeList[item.AccountType] = item.AccountType;
            }
        }

        return memo;
    }, []);

    return <div>

        <button
            onClick={onToggleSectionClick}
            className={'w-full pl-3 hover:bg-tm-gray-100 space-x-3 whitespace-nowrap py-4 pr-3 text-sm font-medium text-tm-gray-900 flex justify-start items-center cursor-pointer'}
        >
            {!isSectionCollapsed ?
                (<ChevronDownIcon
                    className={`size-4`}/>)
                :
                (<ChevronRightIcon
                    className={`size-4`}/>)
            }

            <span className="text-lg leading-6 font-medium text-tm-gray-900">{name}</span>
        </button>

        {!isSectionCollapsed && (
            <StatementListItemChildren
                columns={columns}
                parentItems={parentItems}
                childrenItems={childrenItems}
                numberOfColumns={numberOfColumns}
                accountTypeList={accountTypeList}
                expandedAllCount={expandedAllCount}
                collapsedAllCount={collapsedAllCount}
                handleToggleAccountDetails={handleToggleAccountDetails}
            />
        )}

        <div
            className="border-y border-tm-gray-200 py-2 mt-3 grid text-base items-center justify-between mb-4 pl-16 pr-2 font-bold"
            style={{gridTemplateColumns: `repeat(${numberOfColumns}, 1fr)`}}
        >
            <div>Total {name}</div>
            {Object.keys(columns).map((key, i) => {
                return <div className="text-right" key={i}>{genericMoneyFormatter(sectionTotals[key])}</div>
            })}
        </div>
    </div>
}

function StatementListItemChildren({
                                       columns,
                                       parentItems,
                                       childrenItems,
                                       numberOfColumns,
                                       expandedAllCount,
                                       collapsedAllCount,
                                       accountTypeList,
                                       handleToggleAccountDetails
                                   }) {

    const [collapsed, setCollapsed] = useState({});

    function handleToggleCollapseClick(accountName) {
        const collapsedUpdate = Object.assign({}, collapsed);
        if (collapsedUpdate[accountName]) {
            delete collapsedUpdate[accountName];
        } else {
            collapsedUpdate[accountName] = accountName;
        }

        setCollapsed(collapsedUpdate);
    }

    useEffect(() => {
        if (expandedAllCount) {
            setCollapsed({});
        }
    }, [expandedAllCount]);

    useEffect(() => {
        if (collapsedAllCount) {
            setCollapsed(accountTypeList);
        }
    }, [collapsedAllCount]);

    return <div className="ml-4">
        {parentItems.map((item) => {
            const accountName = Object.values(item)[0].AccountName;
            const accountID = Object.values(item)[0].AccountID;
            const hasChildren = !!childrenItems[accountID];
            const isSectionCollapsed = !!collapsed[accountName];
            let childrenValues = {};
            if (hasChildren) {
                childrenValues = Object.keys(childrenItems[accountID]).reduce((memo, childCol) => {
                    if (!memo[childCol]) {
                        memo[childCol] = 0;
                    }

                    memo[childCol] += childrenItems[accountID][childCol].Value;

                    return memo;
                }, {});
            }

            return <div key={accountID}>
                <div className="flex justify-start items-center">
                    <div className="relative size-10 flex items-center justify-center">
                        {hasChildren && (
                            <button
                                className="flex items-center justify-center btn-icon"
                                onClick={() => hasChildren && handleToggleCollapseClick(accountName)}
                            >
                                {!isSectionCollapsed ?
                                    (<ChevronDownIcon
                                        className={`size-4`}/>)
                                    :
                                    (<ChevronRightIcon
                                        className={`size-4`}/>)
                                }
                            </button>
                        )}
                    </div>

                    <div
                        className={
                            classNames(
                                "w-full hover:bg-tm-gray-100 pr-2 text-lg leading-6 font-medium text-tm-gray-500 flex justify-start items-center",
                                hasChildren ? "cursor-pointer" : ""
                            )
                        }
                    >
                        <div
                            className={"ml-3 w-full items-center grid"}
                            style={{gridTemplateColumns: `repeat(${numberOfColumns}, 1fr)`}}
                        >
                            <div
                                className="flex items-center gap-4 relative"
                            >
                                {accountName}
                            </div>

                            {Object.keys(columns).map(columnNum => {
                                const displayedValue = isSectionCollapsed ? (item[columnNum]?.Value ?? 0) + childrenValues[columnNum] ?? 0 : (item[columnNum]?.Value ?? 0);
                                if (!hasChildren && displayedValue === 0) {
                                    return null;
                                }
                                return <button
                                    onClick={() => handleToggleAccountDetails(item?.[columnNum], columnNum)}
                                    key={columnNum}
                                    className={"text-right hover:text-primary hover:underline py-2 cursor-pointer"}>{genericMoneyFormatter(displayedValue)}</button>
                            })}
                        </div>
                    </div>
                </div>

                {hasChildren && !isSectionCollapsed && (
                    <>
                        <div
                            className="hover:bg-tm-gray-300 w-full pl-10 pr-2 text-lg leading-6 font-medium text-tm-gray-500 flex justify-start items-center cursor-pointer"
                        >
                            <div
                                className="ml-3 w-full flex items-center grid"
                                style={{gridTemplateColumns: `repeat(${numberOfColumns}, 1fr)`}}
                            >
                                <span
                                    className="pl-8 text-left">{Object.values(childrenItems?.[accountID])?.[0]?.AccountName}</span>

                                {Object.keys(columns).map(columnNum => {
                                    return <button
                                        onClick={() => handleToggleAccountDetails(Object.values(childrenItems?.[accountID])?.[columnNum], columnNum)}
                                        key={columnNum}
                                        className={"text-right hover:text-primary hover:underline py-2 py-2 cursor-pointer"}>{genericMoneyFormatter(childrenItems?.[accountID]?.[columnNum]?.Value ?? 0)}</button>
                                })}
                            </div>
                        </div>
                    </>
                )}

            </div>
        })}
    </div>
}